/**
 * Player API — Purchase, Reveal & Sell.
 *
 * These endpoints run inside the player's browser (your custom UI) and are
 * authorized with the player session token sent as the `X-Platform-Session`
 * request header (the PlatformSession security scheme). They cover the full
 * buy-and-reveal loop: start an order for one or more packs of a game, pay for
 * the order from the player's wallet to reveal the pulled cards, mark a card as
 * revealed, and sell cards back for a buyback credit.
 *
 * Each value below is an OpenAPI 3.1 path item. Successful responses return the
 * real wire shape for the endpoint; failures return the shared error envelope
 * `{ error: 1, error_detail: { ... } }`.
 */
import {
  CUSTOM_PACK_UNAVAILABLE_REASONS,
  CUSTOM_PACK_UNPRICED_NOTE,
  customPackErrorSchema,
  customPackInvalidSelectionExample,
  customPackSelectionItems,
  orderGameKind,
} from './components.customPack';

// Shared by both sell endpoints (by order and from the whole inventory): same
// request flags, same per-card outcome, same sold-card payload.
const SELL_REQUEST_PROPERTIES = {
  all: {
    type: 'boolean',
    description: 'Sell every eligible card. Takes precedence over `pool_item_ids`.',
    example: false,
  },
  pool_item_ids: {
    type: 'array',
    items: { type: 'integer' },
    description: 'Sell only these cards. Used when `all` is not set; ineligible ids are skipped.',
    example: [778812],
  },
  auto_sale: {
    type: 'boolean',
    description:
      "Set to `true` when your platform sells the card on the player's behalf — for example by your own " +
      "auto-sell rule — rather than at the player's request. The sale is then recorded as automatic, and the " +
      '`/credit` callback carries `reason: auto_sale` instead of `sale` (API-only integrations receive no ' +
      '`/credit` for sales). Defaults to `false`.',
    example: false,
  },
};

const SELL_RETRY_NOTE = [
  '**Retries.** Retrying a sell request — for example after a timeout — is safe. Each card is locked while',
  'it is being sold and can be sold only once; on a retry, cards that were already sold are skipped, so they',
  'appear in neither `pool_items` nor `failed`. To confirm the final state, read the player inventory.',
].join('\n');

const SOLD_CARD_SCHEMA = {
  type: 'object',
  title: 'SoldCard',
  properties: {
    id: { type: 'integer', description: 'Card id (`pool_item_id`).' },
    title: { type: 'string', nullable: true, description: 'Card name.' },
    item_price: { type: 'number', nullable: true, description: 'Card price in USD.' },
    opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed (ISO 8601).' },
    badge: {
      type: 'object',
      nullable: true,
      additionalProperties: true,
      description: 'The rarity badge for the card; null when unavailable.',
    },
    images: {
      type: 'array',
      description: 'Card images.',
      items: { $ref: '#/components/schemas/ImageObject' },
    },
    engagement_status: {
      type: 'string',
      nullable: true,
      enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
      description: 'Card state. Typically `buyback` after a sale.',
    },
    fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
    pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
    best_active_offer: {
      type: 'object',
      nullable: true,
      title: 'BuybackOffer',
      description: "The card's buyback offer; null when none.",
      properties: {
        id: { type: 'integer', description: 'Offer id.' },
        status: { type: 'string', description: 'Offer status, e.g. `accepted` after a sale.' },
        gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
        net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
        expires_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer expires (ISO 8601).' },
      },
    },
    sold_from_pool_offer: {
      type: 'object',
      nullable: true,
      title: 'SourceGame',
      description: 'The game (pack) this card was pulled from; null when unavailable.',
      properties: {
        id: { type: 'integer', description: 'Game id.' },
        title: { type: 'string', description: 'Game (pack) name — reflects the pack rename set in the console, when one is configured.' },
        pack_image: { $ref: '#/components/schemas/ImageObject' },
      },
    },
  },
};

const SELL_RESPONSE_200 = {
  description:
    'The sale outcome. `200` even when some or all selected cards could not be sold — check `failed`. ' +
    'Both lists are empty when nothing was eligible to sell.',
  content: {
    'application/json': {
      schema: {
        type: 'object',
        required: ['result'],
        properties: {
          result: {
            type: 'object',
            required: ['pool_items', 'failed'],
            properties: {
              pool_items: {
                type: 'array',
                description: 'The cards that were sold, re-read after the sale.',
                items: SOLD_CARD_SCHEMA,
              },
              failed: {
                type: 'array',
                description:
                  'Cards that were selected but not sold, each with the reason. Treat them as not sold — do not ' +
                  "credit the player for them. Cards still in the player's inventory can be retried.",
                items: {
                  type: 'object',
                  title: 'SellFailure',
                  required: ['id', 'message'],
                  properties: {
                    id: { type: 'integer', description: 'Card id (`pool_item_id`).' },
                    message: { type: 'string', description: 'Why the card was not sold.' },
                  },
                },
              },
            },
          },
        },
      },
      example: {
        result: {
          pool_items: [
            {
              id: 778812,
              title: 'Conor McGregor 2021 Prizm #112',
              item_price: 4.5,
              opened_at: '2026-05-29T14:10:00Z',
              badge: { id: 3, name: 'Rare' },
              images: [{ url: 'https://cdn.example.com/blobs/card.png', metadata: {}, signed_id: 'abc123' }],
              engagement_status: 'buyback',
              fair_market_value: 6.25,
              pool_order_id: 90231,
              best_active_offer: {
                id: 55021,
                status: 'accepted',
                gross_price: 6.25,
                net_price: 5.31,
                expires_at: '2026-05-30T14:03:11Z',
              },
              sold_from_pool_offer: {
                id: 463,
                title: 'HOF ELITE MMA Pack',
                pack_image: { url: 'https://cdn.example.com/blobs/pack.png', metadata: {}, signed_id: 'def456' },
              },
            },
          ],
          failed: [{ id: 778813, message: 'No active offer for this item' }],
        },
      },
    },
  },
};

export const ordersPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/pool_orders': {
    post: {
      tags: ['Purchase & Reveal'],
      summary: 'Start an order',
      description: [
        'Start a purchase round for one or more packs of a game.',
        '',
        'Send the game (pack) id in the path and how many packs to buy in the body.',
        'The order is created in `pending` status with an empty `pool_items` array —',
        'the cards are pulled and revealed only after you pay for the order. Call the',
        'pay endpoint next to charge the wallet and reveal the cards.',
        '',
        'The game must be available to your platform; an unknown or unavailable game',
        'returns `404`.',
        '',
        'A custom pack game (`game_kind: custom_pack`) cannot be bought in two steps: this call answers `422`',
        '`CUSTOM_PACK_INSTANT_ONLY`. Use **Buy a pack (one call)** instead.',
      ].join('\n'),
      operationId: 'createPlayerOrder',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description:
            'The game (pack) id to buy. Must be a game available to your platform; otherwise the request returns 404.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      requestBody: {
        required: false,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                pool_order: {
                  type: 'object',
                  properties: {
                    quantity: {
                      type: 'integer',
                      minimum: 1,
                      default: 1,
                      description:
                        'How many packs to buy. Defaults to 1 when omitted. The effective maximum for a game is its ' +
                        '`max_for_purchase_count` (your per-order limit — 3 unless raised for your account, contact us to raise it — ' +
                        'further reduced by stock). A quantity above it returns 422 (`QUANTITY_LIMIT_EXCEEDED`).',
                      example: 2,
                    },
                  },
                },
              },
            },
            example: { pool_order: { quantity: 2 } },
          },
        },
      },
      responses: {
        '201': {
          description: 'Order started. The order is `pending` and holds no cards until you pay for it.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_order'],
                properties: {
                  pool_order: {
                    type: 'object',
                    title: 'Order',
                    properties: {
                      id: { type: 'integer', description: 'Order id (round id). Use it to pay for, fetch, or sell from this order.' },
                      status: {
                        type: 'string',
                        enum: ['pending', 'paid', 'failed', 'cancelled', 'partially_refunded', 'refunded'],
                        description: 'Order status. A freshly started order is `pending`.',
                      },
                      quantity: { type: 'integer', description: 'Number of packs in the order.' },
                      total_price: {
                        type: 'number',
                        nullable: true,
                        description: 'Total order price in USD. May be null while the order is still being prepared.',
                      },
                      created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the order was started (ISO 8601).' },
                      pool_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'Game',
                        description: 'The game (pack) this order is for.',
                        properties: {
                          id: { type: 'integer', description: 'Game id.' },
                          title: { type: 'string', description: 'Game (pack) name — reflects the pack rename set in the console, when one is configured.' },
                          game_kind: orderGameKind,
                          price: { type: 'number', description: 'Pack price in USD.' },
                          pack_image_url: { type: 'string', format: 'uri', nullable: true, description: 'Pack artwork URL.' },
                          bg_image_url: { type: 'string', format: 'uri', nullable: true, description: 'Background artwork URL, when your platform has set one.' },
                          pack_logo_image_url: { type: 'string', format: 'uri', nullable: true, description: 'Pack logo URL, when your platform has set one.' },
                          pedestal_image_url: { type: 'string', format: 'uri', nullable: true, description: 'Pedestal artwork URL, when your platform has set one.' },
                          status: { type: 'string', description: 'Game availability status.' },
                          max_for_purchase_count: { type: 'integer', description: 'Maximum packs a player may buy in one order.' },
                          estimated_value: { type: 'number', description: 'Estimated value of a pack.' },
                          min_value: { type: 'number', description: 'Lowest possible card value in this pack.' },
                          odds: {
                            type: 'array',
                            description: 'Reveal odds by tier.',
                            items: {
                              type: 'object',
                              properties: {
                                tier_name: { type: 'string', description: 'Tier name, e.g. "Common".' },
                                range: { type: 'string', description: 'Value range for the tier, e.g. "$1 - $5".' },
                                percentage: { type: 'number', description: 'Chance of landing in this tier, as a percentage.' },
                              },
                            },
                          },
                          big_win_chance: { type: 'number', description: 'Combined chance of a card above the common tier, as a percentage.' },
                          pack_video: { type: 'string', format: 'uri', nullable: true, description: 'Pack teaser video URL.' },
                          open_pack_video: { type: 'string', format: 'uri', nullable: true, description: 'Pack reveal video URL.' },
                          web_pack_video: { type: 'string', format: 'uri', nullable: true, description: 'Web pack teaser video URL.' },
                          web_open_pack_video: { type: 'string', format: 'uri', nullable: true, description: 'Web pack reveal video URL.' },
                          operator_currency: { type: 'string', nullable: true, description: 'Display currency for your platform; null on order responses.' },
                          exchange_rate: { type: 'number', nullable: true, description: 'Exchange rate for your platform currency; null on order responses.' },
                        },
                      },
                      pool_items: {
                        type: 'array',
                        description: 'The cards in this order. Empty until you pay for the order; afterward each entry is a revealed card.',
                        items: {
                          type: 'object',
                          title: 'Card',
                          description: 'A card pulled in this order.',
                          properties: {
                            id: { type: 'integer', description: 'Card id.' },
                            title: { type: 'string', description: 'Card name.' },
                            image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL.' },
                            item_price: { type: 'number', description: 'Card price in USD.' },
                            opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed; null until then (ISO 8601).' },
                            fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
                            badge: {
                              type: 'object',
                              nullable: true,
                              title: 'Badge',
                              description: 'Rarity badge for the card; null when unavailable.',
                              properties: {
                                id: { type: 'integer', description: 'Badge id.' },
                                name: { type: 'string', description: 'Badge name, e.g. "Rare".' },
                              },
                            },
                            best_active_offer: {
                              type: 'object',
                              nullable: true,
                              title: 'BuybackOffer',
                              description: "The card's current buyback offer; null when none.",
                              properties: {
                                id: { type: 'integer', description: 'Offer id.' },
                                gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
                                net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
                                buyback_rate: { type: 'number', nullable: true, description: 'Buyback rate as a percentage; null when not set.' },
                                auto_sell_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card auto-sells if not actioned (ISO 8601).' },
                                created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer was created (ISO 8601).' },
                              },
                            },
                            status: {
                              type: 'string',
                              nullable: true,
                              enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                              description: 'Card state. `on_hands` means it is in the player inventory and eligible to sell.',
                            },
                            pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                pool_order: {
                  id: 90231,
                  status: 'pending',
                  quantity: 2,
                  total_price: 19.98,
                  created_at: '2026-05-29T14:03:11Z',
                  pool_offer: {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: 9.99,
                    pack_image_url: 'https://cdn.example.com/blobs/pack.png',
                    bg_image_url: null,
                    pack_logo_image_url: null,
                    pedestal_image_url: null,
                    status: 'published',
                    max_for_purchase_count: 3,
                    estimated_value: 12.0,
                    min_value: 1,
                    odds: [
                      { tier_name: 'Common', range: '$1 - $5', percentage: 70 },
                      { tier_name: 'Rare', range: '$50+', percentage: 2.5 },
                    ],
                    big_win_chance: 30,
                    pack_video: 'https://cdn.example.com/blobs/pack.mp4',
                    open_pack_video: 'https://cdn.example.com/blobs/open.mp4',
                    web_pack_video: null,
                    web_open_pack_video: null,
                    operator_currency: null,
                    exchange_rate: null,
                  },
                  pool_items: [],
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Game not found or not available to your platform.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            'The order could not be started (for example, the pack is out of stock). `QUANTITY_LIMIT_EXCEEDED` when `quantity` ' +
            'is above your operator-configured maximum. `CUSTOM_PACK_INSTANT_ONLY` when the game is a custom pack, which ' +
            'can only be bought with **Buy a pack (one call)**.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error. Retry or contact support with the error id from the message.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_orders/{id}': {
    get: {
      tags: ['Purchase & Reveal'],
      summary: 'Fetch an order',
      description: [
        'Fetch a single order belonging to the current player.',
        '',
        'Use this to poll order state and read its cards. While the order is',
        '`pending` the `pool_items` array is empty; after you pay for the order the',
        'status becomes `paid` and `pool_items` holds the revealed cards together with',
        'their value, badge, and buyback offer.',
        '',
        'An order that does not belong to the current player returns `404`.',
      ].join('\n'),
      operationId: 'getPlayerOrder',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Order id (round id). Must belong to the current player; otherwise the request returns 404.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: 'The order with its game and any revealed cards.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_order'],
                properties: {
                  pool_order: {
                    type: 'object',
                    title: 'Order',
                    properties: {
                      id: { type: 'integer', description: 'Order id (round id).' },
                      status: {
                        type: 'string',
                        enum: ['pending', 'paid', 'failed', 'cancelled', 'partially_refunded', 'refunded'],
                        description: 'Order status.',
                      },
                      quantity: { type: 'integer', description: 'Number of packs in the order.' },
                      total_price: { type: 'number', nullable: true, description: 'Total order price in USD.' },
                      created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the order was started (ISO 8601).' },
                      pool_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'Game',
                        description: 'The game (pack) this order is for. See the start-an-order response for the full field list.',
                        properties: {
                          id: { type: 'integer', description: 'Game id.' },
                          title: { type: 'string', description: 'Game (pack) name — reflects the pack rename set in the console, when one is configured.' },
                          game_kind: orderGameKind,
                          price: { type: 'number', description: 'Pack price in USD.' },
                          pack_image_url: { type: 'string', format: 'uri', nullable: true },
                          bg_image_url: { type: 'string', format: 'uri', nullable: true },
                          pack_logo_image_url: { type: 'string', format: 'uri', nullable: true },
                          pedestal_image_url: { type: 'string', format: 'uri', nullable: true },
                          status: { type: 'string', description: 'Game availability status.' },
                          max_for_purchase_count: { type: 'integer', description: 'Maximum packs per order.' },
                          estimated_value: { type: 'number', description: 'Estimated value of a pack.' },
                          min_value: { type: 'number', description: 'Lowest possible card value.' },
                          odds: {
                            type: 'array',
                            description: 'Reveal odds by tier.',
                            items: {
                              type: 'object',
                              properties: {
                                tier_name: { type: 'string' },
                                range: { type: 'string' },
                                percentage: { type: 'number' },
                              },
                            },
                          },
                          big_win_chance: { type: 'number', description: 'Combined chance of an above-common card, as a percentage.' },
                          pack_video: { type: 'string', format: 'uri', nullable: true },
                          open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          operator_currency: { type: 'string', nullable: true, description: 'Display currency for your platform; null on order responses.' },
                          exchange_rate: { type: 'number', nullable: true, description: 'Exchange rate for your platform currency; null on order responses.' },
                        },
                      },
                      fairness: {
                        $ref: '#/components/schemas/FairnessReceipt',
                        description:
                          'Provably-fair purchases only — absent on every other order. The signed ticket, both seeds and each ' +
                          'card slot’s roll; see *Provably fair* in the overview to verify it.',
                      },
                      pool_items: {
                        type: 'array',
                        description: 'The cards in this order. Empty until the order is paid; afterward each entry is a revealed card.',
                        items: {
                          type: 'object',
                          title: 'Card',
                          description: 'A card pulled in this order.',
                          properties: {
                            id: { type: 'integer', description: 'Card id.' },
                            title: { type: 'string', description: 'Card name.' },
                            image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL.' },
                            item_price: { type: 'number', description: 'Card price in USD.' },
                            opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed; null until then (ISO 8601).' },
                            fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
                            badge: {
                              type: 'object',
                              nullable: true,
                              title: 'Badge',
                              description: 'Rarity badge for the card; null when unavailable.',
                              properties: {
                                id: { type: 'integer', description: 'Badge id.' },
                                name: { type: 'string', description: 'Badge name, e.g. "Rare".' },
                              },
                            },
                            best_active_offer: {
                              type: 'object',
                              nullable: true,
                              title: 'BuybackOffer',
                              description: "The card's current buyback offer; null when none.",
                              properties: {
                                id: { type: 'integer', description: 'Offer id.' },
                                gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
                                net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
                                buyback_rate: { type: 'number', nullable: true, description: 'Buyback rate as a percentage; null when not set.' },
                                auto_sell_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card auto-sells if not actioned (ISO 8601).' },
                                created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer was created (ISO 8601).' },
                              },
                            },
                            status: {
                              type: 'string',
                              nullable: true,
                              enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                              description: 'Card state. `on_hands` means it is in the player inventory and eligible to sell.',
                            },
                            pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                pool_order: {
                  id: 90231,
                  status: 'paid',
                  quantity: 1,
                  total_price: 9.99,
                  created_at: '2026-05-29T14:03:11Z',
                  pool_offer: {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: 9.99,
                    pack_image_url: 'https://cdn.example.com/blobs/pack.png',
                    status: 'published',
                    max_for_purchase_count: 3,
                    estimated_value: 12.0,
                    min_value: 1,
                    odds: [{ tier_name: 'Common', range: '$1 - $5', percentage: 70 }],
                    big_win_chance: 30,
                    operator_currency: null,
                    exchange_rate: null,
                  },
                  pool_items: [
                    {
                      id: 778812,
                      title: 'Conor McGregor 2021 Prizm #112',
                      image_url: 'https://cdn.example.com/blobs/card.png',
                      item_price: 4.5,
                      opened_at: null,
                      fair_market_value: 6.25,
                      badge: { id: 3, name: 'Rare' },
                      best_active_offer: {
                        id: 55021,
                        gross_price: 6.25,
                        net_price: 5.31,
                        buyback_rate: 85.0,
                        auto_sell_at: '2026-05-30T14:03:11Z',
                        created_at: '2026-05-29T14:03:11Z',
                      },
                      status: 'on_hands',
                      pool_order_id: 90231,
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Order not found or not owned by the current player.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_orders/{id}/purchase': {
    post: {
      tags: ['Purchase & Reveal'],
      summary: 'Pay for an order',
      description: [
        "Charge the player's wallet for a pending order and reveal the cards.",
        '',
        'On success the order status becomes `paid` and `pool_items` is filled with',
        "the cards pulled from the pack, each carrying its value and a buyback offer.",
        '',
        'This call is safe to retry: paying for an order that is already `paid` returns',
        'the same order unchanged without charging the wallet again. The order must',
        'belong to the current player; otherwise the request returns `404`. If the',
        'wallet cannot cover the order the request returns `422`.',
        '',
        'The response uses the same shape as fetching an order.',
        '',
        'Accepts the same optional `fairness` object as **Buy a pack (one call)**.',
        '',
        'An order of a custom pack game cannot be paid in a separate step: this call answers `422`',
        '`CUSTOM_PACK_INSTANT_ONLY`. A custom pack is bought with **Buy a pack (one call)** only.',
      ].join('\n'),
      operationId: 'payForPlayerOrder',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Order id (round id) to pay for. Must belong to the current player; otherwise the request returns 404.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      requestBody: {
        required: false,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                fairness: {
                  type: 'object',
                  description:
                    'Provably fair (only while it is on for your platform): the ticket from **Get a ticket** and a fresh player ' +
                    'seed. The ticket is checked before the wallet is touched; a refusal (`FAIRNESS_*`) charges nothing. Omit ' +
                    'to buy as usual — unless your platform requires provably fair (`FAIRNESS_REQUIRED`).',
                  required: ['ticket_id', 'client_seed'],
                  properties: {
                    ticket_id: { type: 'string', pattern: '^[0-9a-f]{32}$', description: '`ticket.id` from **Get a ticket**.' },
                    client_seed: {
                      type: 'string',
                      pattern: '^[0-9a-fA-F]{64}$',
                      description: 'The player seed: 32 random bytes as 64 hex characters, new for every purchase.',
                    },
                  },
                },
                pool_order: {
                  type: 'object',
                  properties: {
                    volatility: {
                      type: 'string',
                      enum: ['normal', 'high', 'max'],
                      default: 'normal',
                      description:
                        "Risk level chosen by the player, applied to this order's reveal. Higher levels keep the same expected value but widen the spread of outcomes. Unknown or omitted values fall back to \"normal\".",
                      example: 'max',
                    },
                  },
                },
              },
            },
            example: { pool_order: { volatility: 'max' } },
          },
        },
      },
      responses: {
        '200': {
          description: 'Order paid. Status is `paid` and the revealed cards are returned in `pool_items`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_order'],
                properties: {
                  pool_order: {
                    type: 'object',
                    title: 'Order',
                    properties: {
                      id: { type: 'integer', description: 'Order id (round id).' },
                      status: {
                        type: 'string',
                        enum: ['pending', 'paid', 'failed', 'cancelled', 'partially_refunded', 'refunded'],
                        description: 'Order status. Expected `paid` after a successful charge. An already-paid order is returned unchanged.',
                      },
                      quantity: { type: 'integer', description: 'Number of packs in the order.' },
                      total_price: { type: 'number', nullable: true, description: 'Total order price in USD.' },
                      created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the order was started (ISO 8601).' },
                      pool_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'Game',
                        description: 'The game (pack) this order is for. See the start-an-order response for the full field list.',
                        properties: {
                          id: { type: 'integer' },
                          title: { type: 'string' },
                          game_kind: orderGameKind,
                          price: { type: 'number' },
                          pack_image_url: { type: 'string', format: 'uri', nullable: true },
                          bg_image_url: { type: 'string', format: 'uri', nullable: true },
                          pack_logo_image_url: { type: 'string', format: 'uri', nullable: true },
                          pedestal_image_url: { type: 'string', format: 'uri', nullable: true },
                          status: { type: 'string' },
                          max_for_purchase_count: { type: 'integer' },
                          estimated_value: { type: 'number' },
                          min_value: { type: 'number' },
                          odds: {
                            type: 'array',
                            items: {
                              type: 'object',
                              properties: {
                                tier_name: { type: 'string' },
                                range: { type: 'string' },
                                percentage: { type: 'number' },
                              },
                            },
                          },
                          big_win_chance: { type: 'number' },
                          pack_video: { type: 'string', format: 'uri', nullable: true },
                          open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          operator_currency: { type: 'string', nullable: true },
                          exchange_rate: { type: 'number', nullable: true },
                        },
                      },
                      fairness: {
                        $ref: '#/components/schemas/FairnessReceipt',
                        description:
                          'Provably-fair purchases only — absent on every other order. The signed ticket, both seeds and each ' +
                          'card slot’s roll; see *Provably fair* in the overview to verify it.',
                      },
                      pool_items: {
                        type: 'array',
                        description: 'The revealed cards pulled for this order.',
                        items: {
                          type: 'object',
                          title: 'Card',
                          description: 'A card pulled in this order.',
                          properties: {
                            id: { type: 'integer', description: 'Card id.' },
                            title: { type: 'string', description: 'Card name.' },
                            image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL.' },
                            item_price: { type: 'number', description: 'Card price in USD.' },
                            opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed; null until then (ISO 8601).' },
                            fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
                            badge: {
                              type: 'object',
                              nullable: true,
                              title: 'Badge',
                              description: 'Rarity badge for the card; null when unavailable.',
                              properties: {
                                id: { type: 'integer', description: 'Badge id.' },
                                name: { type: 'string', description: 'Badge name, e.g. "Rare".' },
                              },
                            },
                            best_active_offer: {
                              type: 'object',
                              nullable: true,
                              title: 'BuybackOffer',
                              description: "The card's current buyback offer; null when none.",
                              properties: {
                                id: { type: 'integer', description: 'Offer id.' },
                                gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
                                net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
                                buyback_rate: { type: 'number', nullable: true, description: 'Buyback rate as a percentage; null when not set.' },
                                auto_sell_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card auto-sells if not actioned (ISO 8601).' },
                                created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer was created (ISO 8601).' },
                              },
                            },
                            status: {
                              type: 'string',
                              nullable: true,
                              enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                              description: 'Card state. `on_hands` means it is in the player inventory and eligible to sell.',
                            },
                            pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                pool_order: {
                  id: 90231,
                  status: 'paid',
                  quantity: 1,
                  total_price: 9.99,
                  created_at: '2026-05-29T14:03:11Z',
                  pool_offer: {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: 9.99,
                    status: 'published',
                    max_for_purchase_count: 3,
                    estimated_value: 12.0,
                    min_value: 1,
                    odds: [],
                    big_win_chance: 0,
                    operator_currency: null,
                    exchange_rate: null,
                  },
                  pool_items: [
                    {
                      id: 778812,
                      title: 'Conor McGregor 2021 Prizm #112',
                      image_url: 'https://cdn.example.com/blobs/card.png',
                      item_price: 4.5,
                      opened_at: null,
                      fair_market_value: 6.25,
                      badge: { id: 3, name: 'Rare' },
                      best_active_offer: {
                        id: 55021,
                        gross_price: 6.25,
                        net_price: 5.31,
                        buyback_rate: 85.0,
                        auto_sell_at: null,
                        created_at: '2026-05-29T14:03:11Z',
                      },
                      status: 'on_hands',
                      pool_order_id: 90231,
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Order not found or not owned by the current player.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description:
            'The charge could not be completed (for example, insufficient wallet balance). `CUSTOM_PACK_INSTANT_ONLY` ' +
            'when the order is for a custom pack, which can only be bought with **Buy a pack (one call)**.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_offers/{pool_offer_id}/instant_purchase': {
    post: {
      tags: ['Purchase & Reveal'],
      summary: 'Buy a pack (one call)',
      description: [
        'Start an order and pay for it in a single call.',
        '',
        'This collapses the two-step start-an-order + pay-for-an-order flow into one',
        'request: send the game (pack) id in the path and how many packs to buy in the',
        'body, and the wallet is charged and the cards are pulled in the same call. On',
        'success the order status is `paid` and `pool_items` holds the pulled cards.',
        '',
        'The cards are pulled but **not yet revealed** — each one comes back with',
        '`opened_at: null` and `best_active_offer: null`; its rarity `badge` is already set. Reveal them one at',
        'a time with the reveal-a-card endpoint as the player flips each card in your UI;',
        'the reveal step is unchanged. Set `pool_order.open_immediately` to `true` to have them opened (and offered',
        'for buyback) right after payment instead.',
        '',
        '**Idempotency.** Send an optional `X-Idempotency-Key` header — a client-chosen',
        'key — to make the purchase safe to retry. A retry that reuses the same key',
        'returns the same order without charging the wallet again. A second request that',
        'arrives with the same key while the first is still in flight returns `409`',
        '(`INSTANT_PURCHASE_IN_PROGRESS`); retry once the first call has resolved.',
        'A key is remembered for one hour; after that the same key starts a new purchase.',
        '',
        '**Money is atomic.** If anything fails after the wallet is charged, the charge is',
        'reversed with a compensating refund to the operator and the order is rolled back,',
        'so a `5xx` never leaves the player charged for a pack they did not get — the call',
        'is safe to retry.',
        '',
        '**Jackpot award (API-only integrations).** Include an optional `jackpot` object to',
        'award one of your active jackpot cards on this purchase — we place that card into',
        'the pack, replacing one normal pull. This is the API-only counterpart of the',
        '`jackpot` instruction in the `/debit` webhook response (API-only integrations do',
        'not receive `/debit`). It is accepted **only** when your integration runs in',
        'API-only mode; for every other integration the request returns `403`',
        '(`JACKPOT_NOT_ALLOWED`) — pass the instruction in your `/debit` response instead.',
        'The card must be one of your active jackpot cards (see *List active jackpot',
        'cards*); an unknown, foreign or inactive card returns `422`',
        '(`JACKPOT_CARD_UNRESOLVED`) before any money moves. A retried purchase that',
        'reuses the same `X-Idempotency-Key` returns the original order — the jackpot',
        'instruction is honored on the first execution only, so retries must resend the',
        'identical request.',
        '',
        '**Provably fair.** While it is on for your platform, send `fairness: { ticket_id, client_seed }`',
        '(see **Get a ticket**): every card’s rarity tier is then rolled from the two seeds and the odds fixed in',
        'the ticket, the response carries `fairness` with both seeds, and the purchase can be verified. A purchase',
        'that retries with the same `X-Idempotency-Key` after a ticket refusal needs a **new** ticket.',
        '',
        '**Custom packs.** For a game whose `game_kind` is `custom_pack` (see **Get a game**) the body carries the',
        'player’s selection instead of a quantity: `custom_pack.items` (the cards and their chances, as sent to',
        '**Get a custom pack quote**) and `custom_pack.expected_price_cents`, the `price_cents` the player was shown.',
        'The order always holds a single pack (`quantity` is 1) and one card is drawn from the selection;',
        '`pool_order.quantity` and `volatility` do not apply. The selection is priced again before any money moves:',
        'when the price is no longer `expected_price_cents` the call answers `409` `CUSTOM_PACK_PRICE_CHANGED` with',
        'the fresh `quote` and charges nothing — show the new price and, once the player confirms, send the call',
        'again with it. An invalid selection answers `422` `CUSTOM_PACK_INVALID_SELECTION`, also before any charge.',
        'If the card drawn was taken by another player after the wallet was charged, the charge is refunded, the',
        'order is rolled back and the call answers `422` `CARD_UNAVAILABLE`. When another purchase is holding one of',
        'the selected cards for longer than we wait, the charge is refunded the same way and the call answers `409`',
        '`CUSTOM_PACK_BUSY`: the player may simply try again in a moment. When custom packs cannot be opened at all',
        'right now (for example the server cannot seed the roll), the call answers `422` `CUSTOM_PACK_UNAVAILABLE`',
        'before any money moves. A custom pack is never provably fair and never a jackpot award: `fairness` and',
        '`jackpot` are refused with `422` (`CUSTOM_PACK_FAIRNESS_UNSUPPORTED`, `JACKPOT_NOT_ALLOWED`).',
        '`X-Idempotency-Key` works as for regular packs, with one addition: a custom pack’s key belongs to the player',
        'and to the purchase it first carried — the game, the selection with its chances and `expected_price_cents`.',
        'Sent again for that same purchase it returns the same order, without a second charge; sent with anything',
        'else it answers `409` `IDEMPOTENCY_KEY_REUSED` and charges nothing, so give every new selection or price a',
        'new key. The response is the usual order: `quantity` is 1, `total_price` is the price of the selection and',
        '`pool_items` holds the one card drawn.',
        '',
        'The game must be available to your platform; an unknown or unavailable game',
        'returns `404`. The response uses the same shape as paying for an order.',
      ].join('\n'),
      operationId: 'instantPurchasePlayerOrder',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description:
            'The game (pack) id to buy. Must be a game available to your platform; otherwise the request returns 404.',
        },
        {
          name: 'X-Idempotency-Key',
          in: 'header',
          required: false,
          schema: { type: 'string' },
          description:
            'Optional client-chosen key that deduplicates a retried purchase within a retry window of one hour. Reusing the same key returns the same order without charging the wallet again; a concurrent retry with the same key returns 409. For a custom pack the key is also tied to the purchase it first carried: another selection, price or game under the same key returns 409 (`IDEMPOTENCY_KEY_REUSED`).',
          example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      requestBody: {
        required: false,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                pool_order: {
                  type: 'object',
                  properties: {
                    quantity: {
                      type: 'integer',
                      minimum: 1,
                      default: 1,
                      description:
                        'How many packs to buy. Defaults to 1 when omitted. The effective maximum for a game is its ' +
                        '`max_for_purchase_count` (your per-order limit — 3 unless raised for your account, contact us to raise it — ' +
                        'further reduced by stock). A quantity above it returns 422 (`QUANTITY_LIMIT_EXCEEDED`); nothing is charged.',
                      example: 1,
                    },
                    volatility: {
                      type: 'string',
                      enum: ['normal', 'high', 'max'],
                      default: 'normal',
                      description:
                        'Risk level chosen by the player. Higher levels keep the same expected value but widen the spread of outcomes (more Common and a fatter top tier, a thinner middle). Unknown or omitted values fall back to "normal".',
                      example: 'max',
                    },
                    open_immediately: {
                      type: 'boolean',
                      default: false,
                      description:
                        'When `true`, the pulled cards are opened and offered for buyback right after payment, so the response ' +
                        'already carries `opened_at` and `best_active_offer` for each card; otherwise they come back unopened ' +
                        'and are revealed one at a time. Best-effort: if opening a card fails the purchase still succeeds ' +
                        'and that card stays unopened. Works the same for custom packs.',
                      example: false,
                    },
                  },
                },
                custom_pack: {
                  type: 'object',
                  description:
                    'The player’s selection and the price they confirmed. Required for a game whose `game_kind` is ' +
                    '`custom_pack`; without it the call answers `422` `CUSTOM_PACK_INVALID_SELECTION` ' +
                    '(`SELECTION_MALFORMED`) and charges nothing.',
                  required: ['items', 'expected_price_cents'],
                  properties: {
                    items: customPackSelectionItems,
                    expected_price_cents: {
                      type: 'integer',
                      description:
                        'The `price_cents` of the **Get a custom pack quote** the player confirmed. The selection is priced ' +
                        'again before any charge; when the price differs, nothing is charged and the call answers `409` with ' +
                        'the new quote.',
                      example: 32667,
                    },
                  },
                },
                fairness: {
                  type: 'object',
                  description:
                    'Provably fair (only while it is on for your platform): the ticket from **Get a ticket** and a fresh player ' +
                    'seed. The ticket is checked before the wallet is touched; a refusal (`FAIRNESS_*`) charges nothing. Omit ' +
                    'to buy as usual — unless your platform requires provably fair (`FAIRNESS_REQUIRED`).',
                  required: ['ticket_id', 'client_seed'],
                  properties: {
                    ticket_id: { type: 'string', pattern: '^[0-9a-f]{32}$', description: '`ticket.id` from **Get a ticket**.' },
                    client_seed: {
                      type: 'string',
                      pattern: '^[0-9a-fA-F]{64}$',
                      description: 'The player seed: 32 random bytes as 64 hex characters, new for every purchase.',
                    },
                  },
                },
                jackpot: {
                  type: 'object',
                  description:
                    'API-only integrations only. Include only when this purchase awards a jackpot card. Names the specific card to place into the ' +
                    'pack, replacing one normal pull. The card must be one of your active jackpot cards (see GET /jackpot_cards) — an unknown, ' +
                    'foreign or inactive card is rejected with 422 before the wallet is touched. Runaway guard: at most 3000 jackpot awards ' +
                    'per operator per calendar day (UTC) — an instruction beyond that is skipped and a normal card is drawn. For every ' +
                    'non-API-only integration this object is rejected with 403; pass the instruction in your /debit response instead.',
                  properties: {
                    card_id: { type: 'integer', description: 'Id of the active jackpot card to award.', example: 90123 },
                  },
                },
              },
            },
            examples: {
              loot_pack: { summary: 'Regular pack', value: { pool_order: { quantity: 1, volatility: 'max' } } },
              custom_pack: {
                summary: 'Custom pack',
                value: {
                  custom_pack: { items: customPackSelectionItems.example, expected_price_cents: 32667 },
                  pool_order: { open_immediately: false },
                },
              },
            },
          },
        },
      },
      responses: {
        '200': {
          description:
            'Pack bought and paid. Status is `paid` and the pulled cards are returned in `pool_items` — still unrevealed (`opened_at: null`, `best_active_offer: null`) until you reveal each one, unless `pool_order.open_immediately` was set; each card’s rarity `badge` is set already. For a custom pack `quantity` is 1 and `pool_items` holds the one card drawn.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_order'],
                properties: {
                  pool_order: {
                    type: 'object',
                    title: 'Order',
                    properties: {
                      id: { type: 'integer', description: 'Order id (round id).' },
                      status: {
                        type: 'string',
                        enum: ['pending', 'paid', 'failed', 'cancelled', 'partially_refunded', 'refunded'],
                        description: 'Order status. Expected `paid` after a successful purchase.',
                      },
                      quantity: { type: 'integer', description: 'Number of packs in the order.' },
                      total_price: { type: 'number', nullable: true, description: 'Total order price in USD.' },
                      created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the order was started (ISO 8601).' },
                      pool_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'Game',
                        description: 'The game (pack) this order is for. See the start-an-order response for the full field list.',
                        properties: {
                          id: { type: 'integer' },
                          title: { type: 'string' },
                          game_kind: orderGameKind,
                          price: { type: 'number' },
                          pack_image_url: { type: 'string', format: 'uri', nullable: true },
                          bg_image_url: { type: 'string', format: 'uri', nullable: true },
                          pack_logo_image_url: { type: 'string', format: 'uri', nullable: true },
                          pedestal_image_url: { type: 'string', format: 'uri', nullable: true },
                          status: { type: 'string' },
                          max_for_purchase_count: { type: 'integer' },
                          estimated_value: { type: 'number' },
                          min_value: { type: 'number' },
                          odds: {
                            type: 'array',
                            items: {
                              type: 'object',
                              properties: {
                                tier_name: { type: 'string' },
                                range: { type: 'string' },
                                percentage: { type: 'number' },
                              },
                            },
                          },
                          big_win_chance: { type: 'number' },
                          pack_video: { type: 'string', format: 'uri', nullable: true },
                          open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_pack_video: { type: 'string', format: 'uri', nullable: true },
                          web_open_pack_video: { type: 'string', format: 'uri', nullable: true },
                          operator_currency: { type: 'string', nullable: true },
                          exchange_rate: { type: 'number', nullable: true },
                        },
                      },
                      fairness: {
                        $ref: '#/components/schemas/FairnessReceipt',
                        description:
                          'Provably-fair purchases only — absent on every other order. The signed ticket, both seeds and each ' +
                          'card slot’s roll; see *Provably fair* in the overview to verify it.',
                      },
                      pool_items: {
                        type: 'array',
                        description: 'The cards pulled for this order. Returned unrevealed: `opened_at` and `best_active_offer` are null until you reveal each card; `badge` is set already.',
                        items: {
                          type: 'object',
                          title: 'Card',
                          description: 'A card pulled in this order.',
                          properties: {
                            id: { type: 'integer', description: 'Card id.' },
                            title: { type: 'string', description: 'Card name.' },
                            image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL.' },
                            item_price: { type: 'number', description: 'Card price in USD.' },
                            opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed; null until then (ISO 8601). Always null here until you reveal the card.' },
                            fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
                            badge: {
                              type: 'object',
                              nullable: true,
                              title: 'Badge',
                              description: 'Rarity badge for the card, set before the card is revealed; null when unavailable. A custom pack card’s badge has only `name`, from the chance the player gave the card.',
                              properties: {
                                id: { type: 'integer', description: 'Badge id.' },
                                name: { type: 'string', description: 'Badge name, e.g. "Rare".' },
                              },
                            },
                            best_active_offer: {
                              type: 'object',
                              nullable: true,
                              title: 'BuybackOffer',
                              description: "The card's current buyback offer; null until the card is revealed.",
                              properties: {
                                id: { type: 'integer', description: 'Offer id.' },
                                gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
                                net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
                                buyback_rate: { type: 'number', nullable: true, description: 'Buyback rate as a percentage; null when not set.' },
                                auto_sell_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card auto-sells if not actioned (ISO 8601).' },
                                created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer was created (ISO 8601).' },
                              },
                            },
                            status: {
                              type: 'string',
                              nullable: true,
                              enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                              description: 'Card state. `on_hands` means it is in the player inventory and eligible to sell.',
                            },
                            pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
                            card_details: { $ref: '#/components/schemas/CardDetails' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              example: {
                pool_order: {
                  id: 90231,
                  status: 'paid',
                  quantity: 1,
                  total_price: 9.99,
                  created_at: '2026-05-29T14:03:11Z',
                  pool_offer: {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: 9.99,
                    status: 'published',
                    max_for_purchase_count: 3,
                    estimated_value: 12.0,
                    min_value: 1,
                    odds: [],
                    big_win_chance: 0,
                    operator_currency: null,
                    exchange_rate: null,
                  },
                  pool_items: [
                    {
                      id: 778812,
                      title: 'Conor McGregor 2021 Prizm #112',
                      image_url: 'https://cdn.example.com/blobs/card.png',
                      item_price: 4.5,
                      opened_at: null,
                      fair_market_value: 6.25,
                      badge: { id: 3, name: 'Rare' },
                      best_active_offer: null,
                      status: 'on_hands',
                      pool_order_id: 90231,
                    },
                  ],
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '403': {
          description:
            '`jackpot.card_id` was sent by a non-API-only integration (`JACKPOT_NOT_ALLOWED`). The jackpot instruction on this endpoint is reserved for API-only integrations; pass it in your `/debit` webhook response instead. (A custom pack refuses any `jackpot` with `422` `JACKPOT_NOT_ALLOWED` instead.)',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description:
            'Game not found or not available to your platform. A custom pack is unavailable as well when ' +
            `${CUSTOM_PACK_UNAVAILABLE_REASONS}. ${CUSTOM_PACK_UNPRICED_NOTE}`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '409': {
          description:
            '`INSTANT_PURCHASE_IN_PROGRESS` — a purchase with the same `X-Idempotency-Key` is already in flight. The ' +
            'wallet is not charged again; retry once the first call has resolved. `CUSTOM_PACK_PRICE_CHANGED` (custom ' +
            'packs) — the price of the selection is no longer `custom_pack.expected_price_cents`. Nothing is charged, and ' +
            'the body carries the fresh `quote` (same shape as **Get a custom pack quote**): show the new price and, ' +
            'if the player confirms, send the call again with it. `CUSTOM_PACK_BUSY` (custom packs) — another ' +
            'purchase was holding one of the selected cards for longer than we wait. The wallet charge is refunded ' +
            'and the order rolled back, so the player is charged nothing; they may simply try again in a moment. ' +
            '`IDEMPOTENCY_KEY_REUSED` (custom packs) — the `X-Idempotency-Key` was first used for a different ' +
            'purchase (another selection, price or game). Nothing is charged, and `pool_order_id` names the order ' +
            'the key first bought, when there is one: send the new purchase under a new key.',
          content: {
            'application/json': {
              schema: customPackErrorSchema,
              examples: {
                price_changed: {
                  summary: 'Custom pack: the price moved',
                  value: {
                    error: 1,
                    error_detail: {
                      code: 'CUSTOM_PACK_PRICE_CHANGED',
                      message: 'The price of this custom pack has changed. Please confirm the new price.',
                    },
                    quote: {
                      price_cents: 33778,
                      ev_cents: 30400,
                      rtp_ppm: 900000,
                      items: [
                        { pool_item_id: 11, chance_permille: 200, fair_market_value_cents: 105000 },
                        { pool_item_id: 12, chance_permille: 200, fair_market_value_cents: 30000 },
                        { pool_item_id: 13, chance_permille: 200, fair_market_value_cents: 10000 },
                        { pool_item_id: 14, chance_permille: 200, fair_market_value_cents: 5000 },
                        { pool_item_id: 15, chance_permille: 200, fair_market_value_cents: 2000 },
                      ],
                    },
                  },
                },
                in_progress: {
                  summary: 'The same purchase is still running',
                  value: {
                    error: 1,
                    error_detail: { code: 'INSTANT_PURCHASE_IN_PROGRESS', message: 'Purchase already in progress.' },
                  },
                },
                busy: {
                  summary: 'Custom pack: the cards were busy (refunded)',
                  value: {
                    error: 1,
                    error_detail: {
                      code: 'CUSTOM_PACK_BUSY',
                      message: 'Those cards are busy right now. Please try again in a moment.',
                    },
                  },
                },
                key_reused: {
                  summary: 'Custom pack: the key was first used for another purchase',
                  value: {
                    error: 1,
                    error_detail: {
                      code: 'IDEMPOTENCY_KEY_REUSED',
                      message: 'This idempotency key was already used for a different purchase.',
                    },
                    pool_order_id: 90231,
                  },
                },
              },
            },
          },
        },
        '422': {
          description:
            'The purchase could not be completed (`INSTANT_PURCHASE_FAILED`) — for example the pack is out of stock. `QUANTITY_LIMIT_EXCEEDED` when `quantity` is above your operator-configured maximum — nothing is charged, lower the quantity and retry. `INSUFFICIENT_BALANCE` when the wallet cannot cover the order — nothing is charged, see *Handling guide → Insufficient balance*. Your own `SPENDING_LIMIT_REACHED*` code when your `/debit` refused over a player spending limit — nothing is charged, `error_detail.message` is your `error_message` (or a default), ready to show; see *Handling guide → Spending limit reached*. Also returned as `JACKPOT_CARD_UNRESOLVED` when `jackpot.card_id` does not match one of your active jackpot cards — no money moves in that case. Provably-fair purchases add the `FAIRNESS_*` codes and `TIER_EXHAUSTED` (listed under **Provably Fair**); a refusal after we saw the player seed carries `fairness.server_seed` next to `error_detail`. ' +
            'For a custom pack: `CUSTOM_PACK_INVALID_SELECTION` — the selection breaks a rule or holds a card that can ' +
            'no longer be picked; `error_detail.details` lists the problems (see **Get a custom pack quote**) and ' +
            'nothing is charged. `CARD_UNAVAILABLE` — the card drawn was taken by another player after the wallet was ' +
            'charged; the charge is refunded and the order rolled back, and `error_detail.details` names the card, so ' +
            'build a new selection without it. `CUSTOM_PACK_FAIRNESS_UNSUPPORTED` — `fairness` was sent, and custom ' +
            'packs are not provably fair. `JACKPOT_NOT_ALLOWED` — `jackpot` was sent, and custom packs do not award ' +
            'jackpots. The last two charge nothing. `CUSTOM_PACK_UNAVAILABLE` — custom packs cannot be opened right now ' +
            '(for example the server cannot seed the roll). It is refused before any money moves, so nothing is ' +
            'charged: try again later. `INSUFFICIENT_BALANCE` and the other codes are as for regular packs.',
          content: {
            'application/json': {
              schema: { anyOf: [{ $ref: '#/components/schemas/ErrorEnvelope' }, customPackErrorSchema] },
              examples: {
                invalid_selection: {
                  summary: 'Custom pack: a card was taken before the charge',
                  value: customPackInvalidSelectionExample,
                },
                card_unavailable: {
                  summary: 'Custom pack: the drawn card was taken after the charge (refunded)',
                  value: {
                    error: 1,
                    error_detail: {
                      code: 'CARD_UNAVAILABLE',
                      message: 'This card was just pulled by another player. Your purchase was refunded.',
                      details: [{ code: 'CARD_UNAVAILABLE', pool_item_id: 11 }],
                    },
                  },
                },
                unavailable: {
                  summary: 'Custom pack: custom packs cannot be opened right now (nothing charged)',
                  value: {
                    error: 1,
                    error_detail: {
                      code: 'CUSTOM_PACK_UNAVAILABLE',
                      message: 'Custom packs are temporarily unavailable. Please try again later.',
                    },
                  },
                },
              },
            },
          },
        },
        '500': {
          description:
            'Unexpected error after the wallet was charged. The charge is reversed with a compensating refund to the operator and the order is rolled back, so the player is never left charged — the call is safe to retry.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_items/{id}/mark_as_opened': {
    put: {
      tags: ['Purchase & Reveal'],
      summary: 'Reveal a card',
      description: [
        'Reveal a single card the player owns and attach its buyback offer.',
        '',
        'Call this when the player flips a card in your UI. The card is stamped with',
        'an `opened_at` time and a buyback offer (`best_active_offer`) the player can',
        'accept later. The card must belong to the current player; an unknown card or',
        'one owned by someone else returns `404`.',
      ].join('\n'),
      operationId: 'revealPlayerCard',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Card id to reveal. Must belong to the current player; otherwise the request returns 404.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: 'The revealed card, now carrying an `opened_at` time and a buyback offer.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_item'],
                properties: {
                  pool_item: {
                    type: 'object',
                    title: 'Card',
                    description: 'A card pulled in an order.',
                    properties: {
                      id: { type: 'integer', description: 'Card id.' },
                      title: { type: 'string', description: 'Card name.' },
                      image_url: { type: 'string', format: 'uri', nullable: true, description: 'Card image URL.' },
                      item_price: { type: 'number', description: 'Card price in USD.' },
                      opened_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card was revealed; null until then (ISO 8601).' },
                      fair_market_value: { type: 'number', nullable: true, description: 'Fair market value of the card in USD.' },
                      badge: {
                        type: 'object',
                        nullable: true,
                        title: 'Badge',
                        description: 'Rarity badge for the card; null when unavailable.',
                        properties: {
                          id: { type: 'integer', description: 'Badge id.' },
                          name: { type: 'string', description: 'Badge name, e.g. "Rare".' },
                        },
                      },
                      best_active_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'BuybackOffer',
                        description: "The card's current buyback offer; null when none.",
                        properties: {
                          id: { type: 'integer', description: 'Offer id.' },
                          gross_price: { type: 'number', description: 'Gross buyback price in USD.' },
                          net_price: { type: 'number', description: 'Net buyback credit to the wallet in USD.' },
                          buyback_rate: { type: 'number', nullable: true, description: 'Buyback rate as a percentage; null when not set.' },
                          auto_sell_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the card auto-sells if not actioned (ISO 8601).' },
                          created_at: { type: 'string', format: 'date-time', nullable: true, description: 'When the offer was created (ISO 8601).' },
                        },
                      },
                      status: {
                        type: 'string',
                        nullable: true,
                        enum: ['unavailable', 'listed', 'on_hands', 'redeemed', 'buyback', 'burned', 'reserved'],
                        description: 'Card state. `on_hands` means it is in the player inventory and eligible to sell.',
                      },
                      pool_order_id: { type: 'integer', description: 'Id of the order (round) this card belongs to.' },
                      sold_from_pool_offer: {
                        type: 'object',
                        nullable: true,
                        title: 'SourceGame',
                        description: 'The game (pack) this card was pulled from; null when unavailable.',
                        properties: {
                          id: { type: 'integer', description: 'Game id.' },
                          title: { type: 'string', description: 'Game (pack) name — reflects the pack rename set in the console, when one is configured.' },
                          image_url: { type: 'string', format: 'uri', nullable: true, description: 'Pack image URL.' },
                        },
                      },
                      card_details: { $ref: '#/components/schemas/CardDetails' },
                    },
                  },
                },
              },
              example: {
                pool_item: {
                  id: 778812,
                  title: 'Conor McGregor 2021 Prizm #112',
                  image_url: 'https://cdn.example.com/blobs/card.png',
                  item_price: 4.5,
                  opened_at: '2026-05-29T14:10:00Z',
                  fair_market_value: 6.25,
                  badge: { id: 3, name: 'Rare' },
                  best_active_offer: {
                    id: 55021,
                    gross_price: 6.25,
                    net_price: 5.31,
                    buyback_rate: 85.0,
                    auto_sell_at: '2026-05-30T14:10:00Z',
                    created_at: '2026-05-29T14:10:00Z',
                  },
                  status: 'on_hands',
                  pool_order_id: 90231,
                  sold_from_pool_offer: {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    image_url: 'https://cdn.example.com/blobs/pack.png',
                  },
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Card not found or not owned by the current player.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description: 'The card could not be revealed.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_orders/{id}/sell': {
    post: {
      tags: ['Sell'],
      summary: 'Sell cards back',
      description: [
        "Sell cards from an order back for a buyback credit to the player's wallet.",
        '',
        "Only cards of this order that are still in the player's inventory (status `on_hands`) and not part",
        'of an active delivery can be sold. Set `all` to true to sell every eligible card in the order, or',
        'pass `pool_item_ids` to sell a specific subset — when `all` is true it takes precedence over',
        '`pool_item_ids`. Ineligible ids (not owned, not part of this order, already sold or in a delivery)',
        'are silently skipped, not rejected: they appear in neither `pool_items` nor `failed`. If you set',
        'neither field — or every requested id is skipped — the call is a no-op and returns empty lists.',
        '',
        'The response is `200` even when a selected card could not be sold: sold cards are returned in',
        '`pool_items`, each reflecting its post-sale state, and cards whose sale did not complete are listed',
        'in `failed` with the reason. Always check `failed`.',
        '',
        'To sell cards from several orders in one request, use **Sell cards from the inventory**.',
        '',
        SELL_RETRY_NOTE,
      ].join('\n'),
      operationId: 'sellPlayerOrderCards',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Order id (round id) to sell cards from. Must belong to the current player; otherwise the request returns 404.',
        },
      ],
      requestBody: {
        required: false,
        content: {
          'application/json': {
            schema: { type: 'object', properties: SELL_REQUEST_PROPERTIES },
            example: { all: false, pool_item_ids: [778812, 778813], auto_sale: false },
          },
        },
      },
      responses: {
        '200': SELL_RESPONSE_200,
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Order not found or not owned by the current player. (Ineligible card ids are silently skipped, not an error.)',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/inventory/sell': {
    post: {
      tags: ['Sell'],
      summary: 'Sell cards from the inventory',
      description: [
        "Sell cards from the player's whole inventory back for a buyback credit, across any number of orders",
        'in one request. Each sold card is credited under its own round, exactly as if it were sold through',
        'its order.',
        '',
        "Only cards that are still in the player's inventory (status `on_hands`) and not part of an active",
        'delivery can be sold. For iframe integrations a card must also have been revealed; API-only',
        'integrations can sell cards that were never opened. Set `all` to true to sell every eligible card,',
        'or pass `pool_item_ids` to sell a specific subset — when `all` is true it takes precedence over',
        '`pool_item_ids`. Ineligible ids are silently skipped, not rejected: they appear in neither',
        '`pool_items` nor `failed`. If you set neither field — or every requested id is skipped — the call is',
        'a no-op and returns empty lists.',
        '',
        'The response is `200` even when a selected card could not be sold: sold cards are returned in',
        '`pool_items`, each reflecting its post-sale state, and cards whose sale did not complete are listed',
        'in `failed` with the reason. Always check `failed`.',
        '',
        SELL_RETRY_NOTE,
      ].join('\n'),
      operationId: 'sellPlayerInventoryCards',
      security: [{ PlatformSession: [] }],
      requestBody: {
        required: false,
        content: {
          'application/json': {
            schema: { type: 'object', properties: SELL_REQUEST_PROPERTIES },
            example: { pool_item_ids: [778812, 778813], auto_sale: true },
          },
        },
      },
      responses: {
        '200': SELL_RESPONSE_200,
        '401': {
          description: 'Missing or invalid player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },
};
